import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import blogPosts from './blogPosts.json';
import Reveal from './Reveal';
import ScrollIndicator from './ScrollIndicator';

/* UI strings — blog content exists in EN/AR; Turkish UI falls back to English content */
const STRINGS = {
    en: {
        back: 'Back to Home',
        category: 'Nutrition',
        by: 'By',
        read: (m) => `${m} min read`,
        related: 'Related Articles',
        notFoundTitle: 'Article Not Found',
        notFoundBody: "Sorry, we couldn't find the article you're looking for.",
        authorBio: 'Registered Dietitian and Founder of Sara Khedr Clinical Nutrition Clinic',
        readArticle: 'Read article',
    },
    ar: {
        back: 'العودة للرئيسية',
        category: 'تغذية',
        by: 'بواسطة',
        read: (m) => `${m} دقائق قراءة`,
        related: 'مقالات ذات صلة',
        notFoundTitle: 'المقال غير موجود',
        notFoundBody: 'عذراً، لا يمكننا العثور على المقال المطلوب.',
        authorBio: 'أخصائية تغذية مسجلة ومؤسسة عيادة سارة خضر للتغذية العلاجية',
        readArticle: 'اقرأ المقال',
    },
    tr: {
        back: 'Ana Sayfaya Dön',
        category: 'Beslenme',
        by: '',
        read: (m) => `${m} dk okuma`,
        related: 'İlgili Yazılar',
        notFoundTitle: 'Yazı Bulunamadı',
        notFoundBody: 'Üzgünüz, aradığınız yazıyı bulamadık.',
        authorBio: 'Kayıtlı Diyetisyen ve Sara Khedr Klinik Beslenme Kliniği Kurucusu',
        readArticle: 'Yazıyı oku',
    },
};

/* Inline **bold** renderer */
const Bold = ({ text }) =>
    text.split('**').map((part, i) =>
        i % 2 === 0 ? (
            part
        ) : (
            <strong key={i} className="font-semibold text-primary">
                {part}
            </strong>
        )
    );

const BlogDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const contentLang = i18n.language === 'ar' ? 'ar' : 'en';
    const ui = STRINGS[i18n.language] || STRINGS.en;
    const isRTL = i18n.language === 'ar';

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    const post = blogPosts.find((p) => p.id === parseInt(id, 10));

    const handleBackToHome = () => {
        navigate('/', { replace: true });
        setTimeout(() => {
            const blogSection = document.getElementById('blog');
            if (blogSection) blogSection.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    const formatDate = (dateString) =>
        new Date(dateString).toLocaleDateString(isRTL ? 'ar-EG' : i18n.language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });

    const imgSrc = (image) => (image.startsWith('http') ? image : `/${image}`);

    if (!post) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-mist px-5">
                <div className="sk-card max-w-md w-full p-10 text-center">
                    <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-soft text-primary text-2xl mb-6">
                        <i className="fas fa-file-circle-question" aria-hidden="true" />
                    </span>
                    <h2 className="font-heading text-2xl font-bold text-primary-deep mb-3">{ui.notFoundTitle}</h2>
                    <p className="text-ink-soft mb-8">{ui.notFoundBody}</p>
                    <button type="button" onClick={handleBackToHome} className="sk-btn sk-btn--primary">
                        <i className={`fas fa-arrow-${isRTL ? 'right' : 'left'}`} aria-hidden="true" />
                        {ui.back}
                    </button>
                </div>
            </div>
        );
    }

    const copy = post[contentLang];
    const readingMinutes = Math.max(1, Math.round(copy.content.split(/\s+/).length / 200));
    const relatedPosts = blogPosts.filter((p) => p.id !== post.id).slice(0, 3);

    const renderBody = (body, key) => {
        if (!body) return null;

        // Numbered lists
        if (body.match(/^\d+\./m)) {
            const items = body.split('\n').filter((item) => item.match(/^\d+\./));
            return (
                <ol key={key} className="my-7 space-y-3 list-none p-0">
                    {items.map((item, i) => (
                        <li key={i} className="flex items-start gap-4 bg-mist border border-line rounded-2xl px-5 py-4">
                            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-heading font-semibold text-[0.9rem] flex-shrink-0">
                                {i + 1}
                            </span>
                            <span className="text-ink leading-relaxed pt-1">
                                <Bold text={item.replace(/^\d+\.\s*/, '')} />
                            </span>
                        </li>
                    ))}
                </ol>
            );
        }

        // Bulleted lists (with optional lead-in paragraph)
        if (body.includes('- ')) {
            const beforeList = body.substring(0, body.indexOf('- ')).trim();
            const items = body.split('\n').filter((item) => item.startsWith('- '));
            return (
                <div key={key}>
                    {beforeList && (
                        <p className="text-ink text-[1.06rem] leading-[1.85] mb-5">
                            <Bold text={beforeList} />
                        </p>
                    )}
                    <ul className="my-5 space-y-2.5 list-none p-0">
                        {items.map((item, i) => (
                            <li key={i} className="flex items-start gap-3">
                                <i className="fas fa-check text-secondary text-[0.8rem] mt-[0.45rem] flex-shrink-0" aria-hidden="true" />
                                <span className="text-ink text-[1.02rem] leading-relaxed">
                                    <Bold text={item.substring(2)} />
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            );
        }

        // Regular paragraphs
        return (
            <p key={key} className="text-ink text-[1.06rem] leading-[1.85] mb-6">
                <Bold text={body} />
            </p>
        );
    };

    const parseContent = (content) =>
        content.split('\n\n').map((section, index) => {
            const lines = section.split('\n');
            // Leading heading line (**Heading** alone or followed by body text)
            const headingMatch = lines[0].match(/^\*\*(.+)\*\*$/);
            const heading = headingMatch ? headingMatch[1] : null;
            const body = heading ? lines.slice(1).join('\n').trim() : section;

            return (
                <React.Fragment key={index}>
                    {heading && (
                        <h2 className="font-heading text-[1.35rem] md:text-[1.55rem] font-semibold text-primary-deep mt-12 mb-5 flex items-baseline gap-3">
                            <span className="w-7 h-[3px] rounded bg-secondary flex-shrink-0 translate-y-[-0.3em]" aria-hidden="true" />
                            {heading}
                        </h2>
                    )}
                    {renderBody(body, `${index}-body`)}
                </React.Fragment>
            );
        });

    return (
        <div className="min-h-screen bg-mist">
            {/* Sticky top bar */}
            <nav
                aria-label="Article navigation"
                className="sticky top-0 z-[100] bg-white/95 backdrop-blur-md border-b border-line"
            >
                <div className="sk-container flex items-center justify-between h-16">
                    <button
                        type="button"
                        onClick={handleBackToHome}
                        className="group inline-flex items-center gap-2.5 font-heading font-semibold text-[0.95rem] text-primary-deep hover:text-primary transition-colors duration-200 bg-transparent border-none cursor-pointer p-2 -ms-2"
                    >
                        <i
                            className={`fas fa-arrow-${isRTL ? 'right' : 'left'} transition-transform duration-200 ${
                                isRTL ? 'group-hover:translate-x-1' : 'group-hover:-translate-x-1'
                            }`}
                            aria-hidden="true"
                        />
                        {ui.back}
                    </button>

                    <span className="flex items-center gap-2.5">
                        <img src="/sk-clinic-logo.png" alt="" className="w-9 h-9 rounded-full bg-white border border-line p-0.5" />
                        <span className="hidden sm:block font-heading font-bold text-primary-deep text-[0.95rem]">Sara Khedr</span>
                    </span>
                </div>
            </nav>

            {/* Reading progress under the 64px sticky bar */}
            <ScrollIndicator offset={64} />

            <main className="sk-container pt-10 pb-16 md:pt-14 md:pb-24">
                {/* Article header — centered editorial measure */}
                <header className="max-w-[46rem] mx-auto">
                    <p className="sk-eyebrow sk-enter" style={{ '--enter-delay': '0ms' }}>
                        {ui.category}
                    </p>
                    <h1
                        className="sk-enter mt-4 font-heading font-bold text-primary-deep leading-[1.15] text-[clamp(1.7rem,4.5vw,2.7rem)]"
                        style={{ '--enter-delay': '80ms' }}
                    >
                        {copy.title}
                    </h1>
                    <p
                        className="sk-enter mt-4 font-heading font-medium text-primary text-[clamp(1.02rem,2vw,1.2rem)] leading-relaxed"
                        style={{ '--enter-delay': '160ms' }}
                    >
                        {copy.subtitle}
                    </p>

                    {/* Meta row */}
                    <div
                        className="sk-enter mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[0.88rem] text-ink-soft"
                        style={{ '--enter-delay': '240ms' }}
                    >
                        <span className="inline-flex items-center gap-2">
                            <i className="fas fa-user-md text-primary" aria-hidden="true" />
                            {ui.by} <strong className="text-primary-deep font-semibold">{post.author}</strong>
                        </span>
                        <span className="inline-flex items-center gap-2">
                            <i className="fas fa-calendar text-primary" aria-hidden="true" />
                            {formatDate(post.date)}
                        </span>
                        <span className="inline-flex items-center gap-2">
                            <i className="fas fa-clock text-primary" aria-hidden="true" />
                            {ui.read(readingMinutes)}
                        </span>
                    </div>
                </header>

                {/* Hero image — wider than the text measure */}
                <Reveal className="max-w-[58rem] mx-auto mt-10">
                    <div className="overflow-hidden rounded-[var(--sk-radius-lg)] border border-line shadow-[var(--sk-shadow-2)] bg-primary-soft">
                        <img
                            src={imgSrc(post.image)}
                            alt={copy.title}
                            width="928"
                            height="520"
                            className="w-full h-auto max-h-[520px] object-cover"
                        />
                    </div>
                </Reveal>

                {/* Body */}
                <article className="max-w-[46rem] mx-auto mt-12">
                    {parseContent(copy.content)}

                    {/* Author card */}
                    <footer className="mt-14 sk-card !rounded-[var(--sk-radius-lg)] p-6 sm:p-7 flex items-center gap-5">
                        <img
                            src="/sk-clinic-logo.png"
                            alt=""
                            width="64"
                            height="64"
                            className="w-16 h-16 rounded-full bg-primary-soft border border-line p-1 flex-shrink-0"
                        />
                        <div>
                            <h3 className="m-0 font-heading font-bold text-primary-deep text-[1.05rem]">{post.author}</h3>
                            <p className="m-0 mt-1 text-ink-soft text-[0.9rem] leading-relaxed">{ui.authorBio}</p>
                        </div>
                    </footer>
                </article>

                {/* Related posts */}
                <aside className="max-w-[58rem] mx-auto mt-16 md:mt-20" aria-label={ui.related}>
                    <Reveal className="mb-8">
                        <p className="sk-eyebrow">{ui.related}</p>
                    </Reveal>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {relatedPosts.map((relatedPost, i) => (
                            <Reveal key={relatedPost.id} delay={i * 70}>
                                <button
                                    type="button"
                                    onClick={() => navigate(`/blog/${relatedPost.id}`)}
                                    className="sk-card sk-card--hover group w-full h-full text-start cursor-pointer overflow-hidden p-0 flex flex-col"
                                >
                                    <div className="overflow-hidden bg-primary-soft">
                                        <img
                                            src={imgSrc(relatedPost.image)}
                                            alt=""
                                            loading="lazy"
                                            width="360"
                                            height="176"
                                            className="w-full h-40 object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-2 p-5">
                                        <span className="text-[0.75rem] font-semibold uppercase tracking-wider text-ink-soft">
                                            {formatDate(relatedPost.date)}
                                        </span>
                                        <h3 className="m-0 font-heading font-semibold text-primary-deep text-[1rem] leading-snug line-clamp-2 transition-colors duration-200 group-hover:text-primary">
                                            {relatedPost[contentLang].title}
                                        </h3>
                                        <p className="m-0 text-ink-soft text-[0.88rem] leading-relaxed line-clamp-2">
                                            {relatedPost[contentLang].subtitle}
                                        </p>
                                        <span className="mt-auto pt-1 inline-flex items-center gap-1.5 text-primary font-heading font-semibold text-[0.82rem]">
                                            {ui.readArticle}
                                            <i
                                                className="fas fa-arrow-right rtl:rotate-180 text-[0.65rem] transition-transform duration-200 group-hover:translate-x-1 rtl:group-hover:-translate-x-1"
                                                aria-hidden="true"
                                            />
                                        </span>
                                    </div>
                                </button>
                            </Reveal>
                        ))}
                    </div>
                </aside>
            </main>
        </div>
    );
};

export default BlogDetails;
